import { currentLocale } from '@/lib/i18n.js';
import { syncServerClock } from '@/lib/server-clock.js';
import { captureError } from '@/lib/monitoring.js';
import { ApiError, ApiTimeoutError } from './errors.js';

export { ApiError, ApiTimeoutError } from './errors.js';

/**
 * Base URL for every Debts API call. Cookie session (unified-access.md
 * section 3: this app's own `dbt_session` cookie, set by `POST /auth/launch`),
 * not a bearer token — there is no local password login any more.
 */
export const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '/api/v1';

const DEFAULT_TIMEOUT_MS = 20_000;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  /**
   * Sent as the `Idempotency-Key` header on routes the contract marks
   * `idempotent` (packages/contracts/src/routes.ts): a repeated request with
   * the same key returns the first result and commits nothing more (SH-13).
   * Callers keep one key per user intent (one dialog opening), not per click.
   */
  idempotencyKey?: string;
  /** multipart/form-data body (file upload); mutually exclusive with `body`. */
  formData?: FormData;
}

async function parseErrorBody(
  response: Response,
): Promise<{ code: string; message: string; details: Record<string, unknown> }> {
  try {
    const payload = (await response.json()) as {
      code?: string;
      message?: string;
      error?: { code?: string; message?: string };
      [key: string]: unknown;
    };
    const code = payload.code ?? payload.error?.code ?? 'UNPROCESSABLE';
    const message = payload.message ?? payload.error?.message ?? 'Request failed';
    // The API wraps failures as `{ success: false, error: { code, message,
    // fieldErrors?, data? } }`. Case-specific payloads (e.g. a conflict's
    // `customerId`) sit one level deeper under `error.data`. Flatten `data`
    // onto `details` so callers can read `error.details['customerId']`
    // without knowing that nesting (ported from unified-access web).
    const errorBody = (payload.error as Record<string, unknown> | undefined) ?? payload;
    const data = errorBody['data'];
    const details =
      data && typeof data === 'object'
        ? { ...errorBody, ...(data as Record<string, unknown>) }
        : errorBody;
    return { code, message, details };
  } catch {
    return { code: 'UNPROCESSABLE', message: 'Request failed', details: {} };
  }
}

/**
 * The one place that calls `fetch` for the Debts web app. Cookie auth
 * (`credentials: 'include'`), a bounded deadline so a hung request never
 * leaves the UI stuck on "جاري الحفظ...", and one error shape callers switch
 * on by `.code`.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    idempotencyKey,
    formData,
  } = options;
  const deadline = new AbortController();
  let timedOut = false;
  const onExternalAbort = (): void => deadline.abort();
  if (signal) {
    if (signal.aborted) deadline.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }
  const timer =
    timeoutMs > 0
      ? setTimeout(() => {
          timedOut = true;
          deadline.abort();
        }, timeoutMs)
      : undefined;

  try {
    let response: Response;
    try {
      response = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: 'include',
        headers: {
          'Accept-Language': currentLocale(),
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(idempotencyKey === undefined ? {} : { 'Idempotency-Key': idempotencyKey }),
        },
        ...(formData === undefined
          ? body === undefined
            ? {}
            : { body: JSON.stringify(body) }
          : { body: formData }),
        signal: deadline.signal,
      });
    } catch (error) {
      throw timedOut ? new ApiTimeoutError() : error;
    }
    if (response.status === 204) return undefined as T;
    if (!response.ok) {
      const { code, message, details } = await parseErrorBody(response);
      const failure = new ApiError(response.status, code, message, details);
      // captureError itself ignores 4xx; a rejected form is not an incident.
      captureError(failure, { path });
      throw failure;
    }
    // Successful responses arrive as `{ success: true, data, meta }`, where
    // `meta` is pagination info as a SIBLING of `data`, not nested inside it
    // (packages/contracts/src/common/envelope.ts's successResponseSchema).
    // A list endpoint's caller expects `{ data, meta }` back (this app's
    // `Paginated<T>`), so when `meta` is present the two are re-combined;
    // a single-record endpoint has `meta: null` and returns `data` bare.
    // Dropping `meta` here (an earlier version of this file did) silently
    // starves every paginated list of its own response shape - callers still
    // "work" but every list renders empty regardless of how much data the
    // server returned, which is easy to miss until exercised live.
    const payload = (await response.json()) as {
      success?: boolean;
      data?: unknown;
      meta?: unknown;
      timestamp?: unknown;
    };
    syncServerClock(payload.timestamp);
    if (payload.success !== true || !('data' in payload)) return payload as T;
    return (
      payload.meta === null || payload.meta === undefined
        ? payload.data
        : { data: payload.data, meta: payload.meta }
    ) as T;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}

/** A fresh key for one user intent (see `RequestOptions.idempotencyKey`). */
export function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

/** Builds a query string from defined, non-empty values only. */
export function toQueryString(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** The file name a `Content-Disposition: attachment; filename="..."` header names. */
export function attachmentName(header: string | null): string | undefined {
  const match = header ? /filename="([^"]+)"/.exec(header) : null;
  return match?.[1] || undefined;
}

/** Export files stay authenticated at download time via the session cookie. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Accept-Language': currentLocale() },
  });
  if (!response.ok) {
    const { code, message, details } = await parseErrorBody(response);
    throw new ApiError(response.status, code, message, details);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = attachmentName(response.headers.get('Content-Disposition')) ?? filename;
  link.click();
  URL.revokeObjectURL(url);
}
